/* eslint-disable react/jsx-max-props-per-line */
/* eslint-disable react/jsx-sort-props */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { Redirect, withRouter } from 'react-router-dom';
import moment from 'moment';
import {
  Box,
  Button,
  DialogContentText,
  Link,
  Stack,
  Typography,
} from '@mui/material';
import FilterListOutlinedIcon from '@mui/icons-material/FilterListOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ReplayIcon from '@mui/icons-material/Replay';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import {
  CustomFilterV2,
  DialogDefaultV2,
  TableV2,
} from '../../../components/v2';
import { renderStatus } from '../../../components/v2/cell-renderers/status';
import BaseService from '../../../services/BaseService';
import TableService from '../../../services/TableService';
import * as selectorsAuth from '../../../store/auth/reducer';
import * as actionsToast from '../../../store/toast/actions';
import { canViewIntegracoesPanel } from '../../../helpers/digitadorReportPermission';
import ChartsRow from './ChartsRow';
import DetalheIntegracao from './DetalheIntegracao';
import ErroBanner from './ErroBanner';
import OverviewKpis from './OverviewKpis';
import {
  canReprocess,
  DEFAULT_SORT_MODEL,
  EMPTY_RESUMO,
  getDigitadorFromBody,
  getStatusCount,
  normalizeFilterForApi,
  normalizeResumo,
  sortModelToApi,
  STATUS_FILTER_OPTIONS,
  statusToChip,
  SYNC_STATUS,
  truncateError,
} from './constants';

const defaultFilter = {
  inicio: moment().subtract(1, 'year').startOf('year').valueOf(),
  fim: moment().endOf('day').valueOf(),
};

const IntegracoesV2 = ({ history, authData, messageSuccess, messageError }) => {
  const [localFilter, setLocalFilter] = useState(defaultFilter);
  const [activeStatus, setActiveStatus] = useState(SYNC_STATUS.ERROR_SYNC);
  const [openFilter, setOpenFilter] = useState(false);
  const [reload, setReload] = useState(false);
  const [resumo, setResumo] = useState(EMPTY_RESUMO);
  const [resumoLoading, setResumoLoading] = useState(false);
  const [detalheRow, setDetalheRow] = useState(null);
  const [openDetalhe, setOpenDetalhe] = useState(false);
  const [reprocessRow, setReprocessRow] = useState(null);
  const [openReprocess, setOpenReprocess] = useState(false);
  const [reprocessLoading, setReprocessLoading] = useState(false);
  const [sortModel, setSortModel] = useState(DEFAULT_SORT_MODEL);

  const canAccess = useMemo(
    () => canViewIntegracoesPanel({ authData, userRole: authData?.role }),
    [authData],
  );

  const apiFilterBase = useMemo(() => {
    const { status: _s, ...rest } = localFilter;
    return normalizeFilterForApi(rest);
  }, [localFilter]);

  const apiFilter = useMemo(() => {
    const status = activeStatus ?? localFilter.status;
    return normalizeFilterForApi(status ? { ...localFilter, status } : localFilter);
  }, [localFilter, activeStatus]);

  const tableFilter = useMemo(
    () => ({ ...apiFilter, ...sortModelToApi(sortModel) }),
    [apiFilter, sortModel],
  );

  const fetchResumo = useCallback(async () => {
    if (!canAccess) return;
    setResumoLoading(true);
    try {
      const response = await TableService.request('integrate-entities/resumo', apiFilterBase);
      setResumo(normalizeResumo(response?.data));
    } catch {
      setResumo(EMPTY_RESUMO);
    } finally {
      setResumoLoading(false);
    }
  }, [apiFilterBase, reload, canAccess]);

  useEffect(() => {
    if (!canAccess) return;
    fetchResumo();
  }, [fetchResumo, canAccess]);

  const sparklineColors = useMemo(
    () => ({
      [SYNC_STATUS.NOT_SYNC]: '#1976d2',
      [SYNC_STATUS.SYNCED]: '#388e3c',
      [SYNC_STATUS.ERROR_SYNC]: '#d32f2f',
    }),
    [],
  );

  if (!canAccess) {
    return <Redirect to="/v2/nsirs" />;
  }

  const errorCount = getStatusCount(resumo.filtrado, SYNC_STATUS.ERROR_SYNC) ?? 0;

  const onReload = () => setReload((prev) => !prev);

  const formatIntegracaoDate = (value) => {
    if (value == null) return null;
    const n = Number(value);
    return moment(!Number.isNaN(n) && n > 0 ? n : value).format('DD/MM/YYYY HH:mm');
  };

  const onReprocessRow = async () => {
    if (!reprocessRow?.id) return;
    setReprocessLoading(true);
    try {
      const response = await BaseService.post({
        url: `integrate-entities/${reprocessRow.id}/reprocessar`,
      });
      messageSuccess({
        message:
          response?.data?.message || 'Integração reprocessada com sucesso',
      });
      setOpenReprocess(false);
      setReprocessRow(null);
      onReload();
    } catch (error) {
      messageError({
        message:
          error?.response?.data?.message
          || error?.message
          || 'Falha ao reprocessar',
      });
    } finally {
      setReprocessLoading(false);
    }
  };

  const columns = [
    {
      field: 'createdAt',
      headerName: 'Data',
      minWidth: 170,
      flex: 1,
      sortable: true,
      renderCell: (params) => {
        const criado = formatIntegracaoDate(params.row.createdAt);
        const sincronizado = formatIntegracaoDate(params.row.syncedAt);
        return (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              height: '100%',
              width: '100%',
            }}
          >
            <Stack spacing={0.25} sx={{ lineHeight: 1.25 }}>
              <Typography variant="body2" component="div">
                {criado || '—'}
              </Typography>
              <Typography variant="body2" component="div" color="text.secondary">
                Sync: {sincronizado || '—'}
              </Typography>
            </Stack>
          </Box>
        );
      },
    },
    {
      field: 'digitadorNome',
      headerName: 'Digitador',
      minWidth: 140,
      flex: 1,
      sortable: true,
      renderCell: (params) =>
        getDigitadorFromBody(params.row.body, params.row.digitadorNome) || '—',
    },
    {
      field: 'nsirCodigo',
      headerName: 'NSIR',
      minWidth: 120,
      flex: 0.9,
      sortable: true,
      renderCell: (params) => {
        const { nsirId, nsirCodigo } = params.row;
        if (!nsirCodigo) return '—';
        if (!nsirId) return nsirCodigo;
        return (
          <Link
            component="button"
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              history.push(`/nsir/${nsirId}`);
            }}
            sx={{ fontWeight: 600 }}
          >
            {nsirCodigo}
          </Link>
        );
      },
    },
    { field: 'nsmpCodigo', headerName: 'NSMP', minWidth: 110, flex: 0.8, sortable: false },
    { field: 'malhaNome', headerName: 'Malha', minWidth: 140, flex: 1, sortable: false },
    {
      field: 'status',
      headerName: 'Status',
      minWidth: 160,
      flex: 1,
      sortable: false,
      renderCell: (params) => renderStatus(statusToChip(params.row.status)),
    },
    {
      field: 'error',
      headerName: 'Erro',
      minWidth: 180,
      flex: 1.2,
      sortable: false,
      renderCell: (params) => truncateError(params.row.error) || '—',
    },
  ];

  const actions = [
    {
      key: 'detalhe',
      title: 'Ver detalhes',
      icon: <VisibilityOutlinedIcon fontSize="small" />,
      onClick: (_id, row) => {
        setDetalheRow(row);
        setOpenDetalhe(true);
      },
    },
    {
      key: 'reprocessar',
      title: 'Reprocessar',
      icon: <ReplayIcon fontSize="small" />,
      show: (row) => canReprocess(row.status),
      onClick: (_id, row) => {
        setReprocessRow(row);
        setOpenReprocess(true);
      },
    },
  ];

  return (
    <Box>
      <Stack spacing={3}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          justifyContent="space-between"
          alignItems={{ xs: 'flex-start', md: 'center' }}
          spacing={2}
          sx={{ pr: 2 }}
        >
          <Box>
            <Typography variant="h4">Integrações</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              NSMPs integradas ao sistema terceiro — visão consolidada e reprocessamento
            </Typography>
          </Box>
          <Button
            variant="outlined"
            startIcon={<FilterListOutlinedIcon />}
            onClick={() => setOpenFilter(true)}
          >
            Filtros
          </Button>
        </Stack>

        <OverviewKpis
          resumo={resumo}
          loading={resumoLoading}
          activeStatus={activeStatus}
          onFilterByStatus={setActiveStatus}
          sparklineColors={sparklineColors}
        />

        <ErroBanner
          errorCount={errorCount}
          apiFilter={apiFilter}
          onReload={onReload}
        />

        <ChartsRow resumo={resumo} resumoLoading={resumoLoading} />

        <Box>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2, pr: 2 }}>
            <InfoOutlinedIcon fontSize="small" color="action" />
            <Typography variant="h6">Detalhamento</Typography>
          </Stack>
          <TableV2
            useApi
            fetchUrl="integrate-entities/filter"
            paginationUrl="/v2/integracoes"
            filterData={tableFilter}
            refreshKey={reload}
            rowHeight={70}
            pageSize={10}
            actions={actions}
            columns={columns}
            emptyMessage="Nenhuma integração encontrada"
            enableColumnSorting
            sortModel={sortModel}
            onSortModelChange={(model) => {
              setSortModel(model.length ? model : DEFAULT_SORT_MODEL);
            }}
          />
        </Box>

        <CustomFilterV2
          variant="dialog"
          open={openFilter}
          onClose={() => setOpenFilter(false)}
          title="Filtros"
          fullWidth
          defaultFilter={defaultFilter}
          appliedFilter={localFilter}
          fields={[
            { field: 'useDates' },
            {
              field: 'useSelectOption',
              key: 'status',
              label: 'Status',
              options: STATUS_FILTER_OPTIONS,
              md: 6,
              xs: 12,
            },
            { field: 'useMalha', md: 6, xs: 12, multiSelect: true },
            { field: 'useDigitador', md: 6, xs: 12, multiSelect: true },
            { field: 'useNsir', md: 12, xs: 12 },
          ]}
          onFilter={(filterData) => {
            setLocalFilter(filterData);
            setActiveStatus(null);
            setOpenFilter(false);
          }}
        />

        <DetalheIntegracao
          open={openDetalhe}
          row={detalheRow}
          onClose={() => {
            setOpenDetalhe(false);
            setDetalheRow(null);
          }}
          onReload={onReload}
        />

        <DialogDefaultV2
          open={openReprocess}
          handlerCancel={() => {
            if (reprocessLoading) return;
            setOpenReprocess(false);
            setReprocessRow(null);
          }}
          handlerConfirm={onReprocessRow}
          title="Reprocessar integração"
          confirmMessage="Sim"
          cancelMessage="Não"
          confirmLoading={reprocessLoading}
          disabledConfirmButton={reprocessLoading}
        >
          <DialogContentText>
            Deseja reenviar a integração
            {reprocessRow?.nsirCodigo ? ` da NSIR ${reprocessRow.nsirCodigo}` : ''}?
            {reprocessLoading ? ' Enviando ao sistema terceiro…' : ''}
          </DialogContentText>
        </DialogDefaultV2>
      </Stack>
    </Box>
  );
};

IntegracoesV2.propTypes = {
  authData: PropTypes.object.isRequired,
  history: PropTypes.shape({
    push: PropTypes.func.isRequired,
  }).isRequired,
  messageSuccess: PropTypes.func.isRequired,
  messageError: PropTypes.func.isRequired,
};

const mapStateToProps = (state) => ({
  authData: selectorsAuth.getAuth(state),
});

const mapDispatchToProps = (dispatch) =>
  bindActionCreators({ ...actionsToast }, dispatch);

export default connect(mapStateToProps, mapDispatchToProps)(withRouter(IntegracoesV2));
